import React, { useEffect, useState, useCallback } from 'react';
import { AttachmentMetadata } from '@codey2/codey-api-types/models';
import { useAttachmentsApi } from '../../../app/api/useAttachmentsApi';
import { useSnackbar } from '../../../navigation/snackbar';
import styles from './ReadOnlyAssetsTab.module.css';

interface ReadOnlyAssetsTabProps {
  exerciseId: number;
}

export const ReadOnlyAssetsTab: React.FC<ReadOnlyAssetsTabProps> = ({ exerciseId }) => {
  const [attachments, setAttachments] = useState<AttachmentMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { getAttachments, downloadAttachment } = useAttachmentsApi();
  const { showSnackbar } = useSnackbar();

  const loadAttachments = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAttachments(exerciseId);
      setAttachments(data);
    } catch {
      showSnackbar('Kon bestanden niet laden. Probeer het later opnieuw.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [exerciseId, getAttachments, showSnackbar]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Bestanden laden...</p>
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>📎 Geen bestanden beschikbaar voor deze opdracht</p>
      </div>
    );
  }

  return (
    <div className={styles.readOnlyAssetsTab}>
      <h3 className={styles.title}>Beschikbare bestanden</h3>
      <p className={styles.description}>Download hier de bestanden die bij deze opdracht horen.</p>

      <div className={styles.attachmentsList}>
        {attachments.map(attachment => (
          <div key={attachment.id} className={styles.attachmentItem}>
            <div className={styles.attachmentIcon}>📄</div>
            <div className={styles.attachmentInfo}>
              <a href={downloadAttachment(exerciseId, attachment.id)} download={attachment.filename} className={styles.attachmentName}>
                {attachment.filename}
              </a>
              <div className={styles.attachmentMeta}>
                <span className={styles.metaItem}>{attachment.contentType}</span>
                <span className={styles.metaDivider}>•</span>
                <span className={styles.metaItem}>{formatFileSize(attachment.size)}</span>
                <span className={styles.metaDivider}>•</span>
                <span className={styles.metaItem}>{formatDate(attachment.uploadedAt)}</span>
              </div>
            </div>
            <a href={downloadAttachment(exerciseId, attachment.id)} download={attachment.filename} className={styles.downloadButton} title="Download">
              ⬇️ Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};
